import DataTable from "@/components/data-table";
import { InviteUserPopup } from "@/components/popups/invite-user";
import { Avatar, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { createFileRoute } from "@tanstack/react-router";
import { useActiveWorkspace } from "@wacht/tanstack-router";
import { ColDef } from "ag-grid-community";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MoreVertical } from "lucide-react";

export const Route = createFileRoute("/_private/team-management")({
  component: RouteComponent,
});

function RouteComponent() {
  const { getMembers, getRoles, inviteMember, addMemberRole, removeMemberRole } =
    useActiveWorkspace();

  const [members, setMembers] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const getMembersRef = useRef(getMembers);
  const getRolesRef = useRef(getRoles);
  const inviteMemberRef = useRef(inviteMember);

  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [roleDialogMode, setRoleDialogMode] = useState<"add" | "remove" | null>(
    null,
  );
  const [activeMember, setActiveMember] = useState<any | null>(null);
  const [selectedRoleValue, setSelectedRoleValue] = useState<string>("");

  useEffect(() => {
    getMembersRef.current = getMembers;
    getRolesRef.current = getRoles;
    inviteMemberRef.current = inviteMember;
  }, [getMembers, getRoles, inviteMember]);

  const fetchMembers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getMembersRef.current?.();
      setMembers(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Failed to fetch members", error);
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRoles = useCallback(async () => {
    try {
      const response = await getRolesRef.current?.();
      setRoles(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error("Failed to fetch roles", error);
      setRoles([]);
    }
  }, []);

  const isAdminMember = useCallback((member: any) => {
    const roles = Array.isArray(member?.roles) ? member.roles : [];
    return roles.some((r: any) => {
      const name = (r?.name ?? r)?.toString().toLowerCase();
      return name === "admin";
    });
  }, []);

  useEffect(() => {
    fetchMembers();
    fetchRoles();
  }, [fetchMembers, fetchRoles]);

  const rows = useMemo(
    () =>
      members.map((member) => ({
        ...member,
        _rowId:
          member?.id ??
          member?.publicMetadata?.externalId ??
          member?.publicUserData?.userId ??
          member?.publicUserData?.identifier,
      })),
    [members],
  );

  const getMemberId = useCallback((member: any) => {
    return (
      member?.id ??
      member?.public_user_data?.user_id ??
      member?.public_user_data?.identifier ??
      member?._rowId
    );
  }, []);

  const openRoleDialog = useCallback(
    (member: any, mode: "add" | "remove") => {
      setActiveMember(member);
      setRoleDialogMode(mode);
      setSelectedRoleValue("");
      setRoleDialogOpen(true);
    },
    [],
  );

  const closeRoleDialog = useCallback(() => {
    setRoleDialogOpen(false);
    setRoleDialogMode(null);
    setActiveMember(null);
    setSelectedRoleValue("");
  }, []);

  const availableRoleOptions = useMemo(() => {
    if (!activeMember || !roleDialogMode) return [];

    if (roleDialogMode === "remove") {
      // only show roles the member currently has
      const memberRoles = Array.isArray(activeMember?.roles)
        ? activeMember.roles
        : [];
      return memberRoles;
    }

    return roles; // add mode => all roles
  }, [activeMember, roleDialogMode, roles]);

  const submitRoleChange = useCallback(async () => {
    if (!activeMember || !roleDialogMode || !selectedRoleValue) return;

    const memberId = getMemberId(activeMember);
    if (!memberId) return;

    try {
      setLoading(true);

      if (roleDialogMode === "add") {
        await addMemberRole?.(memberId, selectedRoleValue);
      } else {
        await removeMemberRole?.(memberId, selectedRoleValue);
      }

      await fetchMembers();
      closeRoleDialog();
    } catch (err) {
      console.error("Role update failed", err);
      // you can add toast here if you use it
    } finally {
      setLoading(false);
    }
  }, [
    activeMember,
    roleDialogMode,
    selectedRoleValue,
    getMemberId,
    addMemberRole,
    removeMemberRole,
    fetchMembers,
    closeRoleDialog,
  ]);

  const columns = useMemo<ColDef<any>[]>(
    () => [
      {
        field: "name",
        headerName: "Member",
        minWidth: 150,
        valueGetter: (params) =>
          `${params.data?.public_user_data?.first_name ?? ""} ${
            params.data?.public_user_data?.last_name ?? ""
          }`.trim(),
        cellRenderer: (params: any) => {
          const user = params.data?.public_user_data ?? {};
          const fullName = `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim();

          return (
            <div className="flex items-center gap-3 h-full">
              {user.profile_picture_url && (
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user.profile_picture_url} />
                </Avatar>
              )}
              <div className="flex flex-col">
                <span className="font-medium">{fullName || "Unknown user"}</span>
              </div>
            </div>
          );
        },
      },
      {
        field: "email",
        headerName: "Email",
        minWidth: 250,
        valueGetter: (params: any) =>
          params.data?.public_user_data?.primary_email_address.email ??
          params.data?.public_user_data?.identifier ??
          "—",
        cellRenderer: (params: any) => (
          <span className="text-sm">
            {params.data?.public_user_data?.primary_email_address.email ??
              params.data?.public_user_data?.identifier ??
              "—"}
          </span>
        ),
      },
      {
        field: "role",
        headerName: "Role",
        minWidth: 140,
        valueGetter: (params: any) => {
          const rs = params.data?.roles;
          if (Array.isArray(rs) && rs.length) {
            return rs
              .map((r: any) => r?.name ?? r)
              .filter(Boolean)
              .join(", ");
          }

          const role = params.data?.role;
          if (role) return role?.name ?? role;

          return "—";
        },
        cellRenderer: (params: any) => (
          <span className="text-sm">{params.value || "—"}</span>
        ),
      },
      {
        field: "permissions",
        headerName: "Permissions",
        minWidth: 220,
        valueGetter: (params: any) => {
          const directPermissions = Array.isArray(params.data?.permissions)
            ? params.data.permissions
            : [];

          const rolePermissions = Array.isArray(params.data?.roles)
            ? params.data.roles.flatMap((role: any) => role?.permissions ?? [])
            : [];

          const all = [...directPermissions, ...rolePermissions].filter(Boolean);
          const unique = Array.from(new Set(all));

          return unique.length ? unique.join(", ") : "—";
        },
        cellRenderer: (params: any) => {
          const directPermissions = Array.isArray(params.data?.permissions)
            ? params.data.permissions
            : [];

          const rolePermissions = Array.isArray(params.data?.roles)
            ? params.data.roles.flatMap((role: any) => role?.permissions ?? [])
            : [];

          const allPermissions = [...directPermissions, ...rolePermissions].filter(
            Boolean,
          );
          const uniquePermissions = Array.from(new Set(allPermissions));

          if (uniquePermissions.length === 0) {
            return <span className="text-sm">—</span>;
          }

          const firstPermission = uniquePermissions[0];
          const remainingCount = uniquePermissions.length - 1;

          if (remainingCount === 0) {
            return <span className="text-sm">{firstPermission}</span>;
          }

          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1 cursor-help">
                    <span className="text-sm">{firstPermission}</span>
                    <span className="text-sm text-muted-foreground">
                      +{remainingCount}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-md">
                  <div className="space-y-1">
                    <div className="font-medium text-sm mb-2">
                      All Permissions ({uniquePermissions.length})
                    </div>
                    <div className="space-y-1">
                      {uniquePermissions.map((permission, index) => (
                        <div key={index} className="text-xs">
                          {permission}
                        </div>
                      ))}
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        },
      },
      {
        field: "createdAt",
        headerName: "Joined On",
        minWidth: 140,
        cellRenderer: (params: any) => {
          const date = params.data?.public_user_data?.created_at
            ? new Date(params.data.public_user_data.created_at)
            : undefined;

          return (
            <span className="text-sm">{date ? date.toLocaleDateString() : "—"}</span>
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        minWidth: 120,
        cellRenderer: (params: any) => {
          const admin = isAdminMember(params.data);
          return (
            <div className="flex items-center h-full">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8" disabled={admin}
              title={admin ? "Admin role can't be changed" : "Edit roles"}>
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => openRoleDialog(params.data, "add")}>
                    Add role
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => openRoleDialog(params.data, "remove")}
                    disabled={!Array.isArray(params.data?.roles) || params.data.roles.length === 0}
                  >
                    Remove role
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ],
    [openRoleDialog],
  );

  return (
    <div className="space-y-4 w-full">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn("text-xl font-semibold")}>Team Management</h1>
          <p className="text-sm text-muted-foreground">
            View and invite members to this workspace.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={fetchMembers}>
            Refresh
          </Button>
          <InviteUserPopup
            onInvite={fetchMembers}
            roles={roles}
            inviteMember={inviteMemberRef.current}
          />
        </div>
      </div>

      <DataTable rowData={rows} columnDefs={columns} height={520} loading={loading} />

      <Dialog open={roleDialogOpen} onOpenChange={(o) => (o ? null : closeRoleDialog())}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {roleDialogMode === "add" ? "Add role" : "Remove role"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">
              {roleDialogMode === "add"
                ? "Select a role to assign to this member."
                : "Select a role to remove from this member."}
            </div>

            <Select value={selectedRoleValue} onValueChange={setSelectedRoleValue}>
              <SelectTrigger>
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                {availableRoleOptions.map((r: any) => {
                  const value = String(r?.id ?? r?.name ?? r);
                  const label = String(r?.name ?? r);
                  return (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeRoleDialog}>
              Cancel
            </Button>
            <Button
              onClick={submitRoleChange}
              disabled={!selectedRoleValue || loading}
            >
              {roleDialogMode === "add" ? "Add" : "Remove"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
